import { mailService } from "src/service/mail.service";
import { setResponseValue } from "../api-response/api-response";
import {
  IComposeEmailData,
  IFolder,
  IMail,
  IMailBoxAuthData,
  IPageToken,
} from "./../../../types/mail";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit/dist";
import { emailFolders } from "src/pages/mail-box/Emails";

export interface IMailBoxSlice {
  currentEmail: IMail | null;
  allEmails: IMail[];
  isLoading: boolean;
  authUrl: string;
  paginationData: IPageToken[];
  next_cursor: string;
  pageNumber: number;
  folders: IFolder[];
  selectedFolder: IFolder | null;
  currentDraft: IMail | null;
  threads: IMail[];
  isThreadsLoading: boolean;
  mailBoxAuthData: IMailBoxAuthData | null;
  emailIntegrations: IMailBoxAuthData[];
}

const initialState: IMailBoxSlice = {
  currentEmail: null,
  allEmails: [],
  isLoading: false,
  authUrl: "",
  paginationData: [],
  next_cursor: "",
  pageNumber: 1,
  folders: [],
  selectedFolder: null,
  currentDraft: null,
  threads: [],
  isThreadsLoading: false,
  mailBoxAuthData: null,
  emailIntegrations: [],
};

const mailBoxSlice = createSlice({
  name: "mailBoxSlice",
  initialState: initialState,
  reducers: {
    setCurrentEmail: (state, action) => {
      state.currentEmail = action.payload;
    },
    setIsLoading: (state, action) => {
      state.isLoading = action.payload;
    },
    setPageNumber: (state, action) => {
      state.pageNumber = action.payload;
    },
    setSelectedFolderId: (state, action) => {
      state.selectedFolder = action.payload;
      state.pageNumber = 1; // Reset page number when folder changes
      state.allEmails = []; // Reset emails when folder changes
      state.paginationData = []; // Reset pagination data when folder changes
      state.next_cursor = ""; // Reset next cursor when folder changes
    },
    resetAllEmails: (state) => {
      return {
        ...state,
        allEmails: [],
        next_cursor: "",
      };
    },
    setThreadsLoading: (state, action) => {
      state.isThreadsLoading = action.payload;
    },
    updatedCurrentThreads: (state, action) => {
      state.threads = state.threads.map((item) => {
        if (item.id === action.payload.id) {
          return {
            ...item,
            starred: !item.starred,
          };
        } else {
          return item;
        }
      });
    },
  },
  extraReducers: (builder) => {
    builder.addCase(GetAllEmails.fulfilled, (state, action) => {
      if (!action.payload) {
        return;
      }
      if (action.payload?.data?.authUrl) {
        return {
          ...state,
          authUrl: action.payload.data.authUrl,
        };
      } else if (action.payload.isThread) {
    
        return {
          ...state,
          threads: action.payload.data.emails
            .filter((item: IMail) => {
              if (
                item.folders.includes("TRASH") || item.folders.includes("Deleted Items") ||
                item.id === state.currentEmail?.id
              ) {
                return false;
              } else {
                return true;
              }
            })
            .sort((a: IMail, b: IMail) => a.date - b.date),
        };
      } else if (Array.isArray(action.payload?.data.emails)) {
        const isExist = state.paginationData.some(
          (item) => item.pageToken === action.payload?.data?.next_cursor
        );
        return {
          ...state,
          allEmails: action.payload.data.emails,
          next_cursor: action.payload.data.next_cursor,
          paginationData: !isExist
            ? [
                ...state.paginationData,
                {
                  pageIndex: state.pageNumber + 1,
                  pageToken: action.payload.data.next_cursor,
                },
              ]
            : [...state.paginationData],
        };
      }
    });
    builder.addCase(GetEmailById.fulfilled, (state, action) => {
      state.currentEmail = action.payload;
    });
    builder.addCase(GetAllFolders.fulfilled, (state, action) => {
      if (!action.payload) {
        return state;
      }

      const emailProvider = state.mailBoxAuthData?.provider;
      // Create a Map for quick lookup
      const folderMap = new Map(
        emailFolders.map((folder) => [
          emailProvider === "microsoft"
            ? folder.outlookname.toLowerCase()
            : folder.gmailName.toLowerCase(),
          folder,
        ])
      );

      // Filter, map, and sort in a single pass
      const filteredFolders = action.payload.folders
        .map((item: IFolder) => {
          const matchedFolder = folderMap.get(item.name.toLowerCase());
          return matchedFolder
            ? {
                ...item,
                icon: matchedFolder.icon,
                displayName:
                  emailProvider === "microsoft"
                    ? matchedFolder.outlookname
                    : matchedFolder.gmailName,
                index: matchedFolder.index,
              }
            : null;
        })
        .filter(Boolean) // Remove null values
        .sort((a: IFolder, b: IFolder) => a.index - b.index); // Sort by index in ascending order

      return {
        ...state,
        folders: filteredFolders,
        selectedFolder:
          state.selectedFolder === null
            ? filteredFolders[0]
            : state.selectedFolder,
      };
    });
    builder.addCase(GetAllDrafts.fulfilled, (state, action) => {
      if (action.payload === undefined) {
        return;
      }
      return {
        ...state,
        allEmails: action.payload.emails,
        next_cursor: action.payload.next_cursor,
      };
    });
    builder.addCase(GetDraftById.fulfilled, (state, action) => {
      state.currentDraft = action.payload;
    });
    builder.addCase(ConnectMailBox.fulfilled, (state, action) => {
      state.authUrl = action.payload?.authUrl || "";
      state.mailBoxAuthData = action.payload?.authData || null;
    });
    builder.addCase(DisConnectMailbox.fulfilled, (state, action) => {
      return {
        ...state,
        authUrl: "",
        isGmailConnected: false,
        folders: [],
        allEmails: [],
        mailBoxAuthData: null,
      };
    });
    builder.addCase(UpdateEmailStatus.fulfilled, (state, action) => {
      return {
        ...state,
        allEmails: state.allEmails.map((item) => {
          if (item.id === action.payload?.mailId) {
            return {
              ...item,
              unread:
                action.payload?.unread !== undefined
                  ? action.payload.unread
                  : item.unread,
              starred:
                action.payload?.starred !== undefined
                  ? action.payload.starred
                  : item.starred,
            };
          } else {
            return item;
          }
        }),
      };
    });
    builder.addCase(GetActiveMailboxProvider.fulfilled, (state, action) => {
      if (action.payload !== undefined) {
        return {
          ...state,
          mailBoxAuthData: action.payload,
        };
      }
    });
    builder.addCase(DeleteMultipleEmails.fulfilled, (state, action) => {
      if (action.payload !== undefined) {
        if (action.payload !== undefined) {
          state.allEmails = state.allEmails.filter(
            (item) => !action.payload?.emailIds.includes(item.id)
          );
          state.threads = state.threads.filter(
            (item) => !action.payload?.emailIds.includes(item.id)
          );
          if (state.selectedFolder) {
            state.selectedFolder.totalCount =
              (state.selectedFolder.totalCount ?? 0) -
              (action.payload?.emailIds.length ?? 0);
            if (state.selectedFolder.totalCount < 0) {
              state.selectedFolder.totalCount = 0;
            }
          }
        }
      }
    });
    builder.addCase(GetAllEmailIntegration.fulfilled, (state, action) => {
      state.emailIntegrations = action.payload;
    });
  },
});

export const GetAllEmails = createAsyncThunk(
  "/getAllEmails",
  async (
    {
      userEmail,
      pageToken,
      folderId,
      searchEmails,
      threadId,
    }: {
      userEmail: string;
      pageToken: string;
      folderId?: string;
      searchEmails?: string;
      threadId?: string;
    },
    { dispatch }
  ) => {
    try {
      if (!threadId) {
        dispatch(setResponseValue({ name: "pending", value: true }));
      } else {
        dispatch(setThreadsLoading(true));
      }
      const { data } = await mailService.getAllEmails(
        userEmail,
        pageToken,
        folderId ?? "",
        searchEmails ?? "",
        threadId ?? ""
      );
      return { data: data, isThread: threadId ? true : false };
    } catch (e: any) {
      console.error("Error fetching emails:", e);
      dispatch(setResponseValue({ name: "error", value: true }));
      dispatch(
        setResponseValue({
          name: "message",
          value: e?.response?.data?.message,
        })
      );
    } finally {
      dispatch(setResponseValue({ name: "pending", value: false }));
      dispatch(setThreadsLoading(false));
    }
  }
);

export const GetEmailById = createAsyncThunk(
  "/getEmailById",
  async (
    { userEmail, emailId }: { userEmail: string; emailId: string },
    { dispatch }
  ) => {
    try {
      dispatch(setIsLoading(true));
      const { data } = await mailService.getEmailById(userEmail, emailId);
      return data.data;
    } catch (e: any) {
      console.error("Error fetching emails:", e);
      dispatch(setResponseValue({ name: "error", value: true }));
      dispatch(
        setResponseValue({
          name: "message",
          value: e?.response?.data?.message,
        })
      );
    } finally {
      dispatch(setIsLoading(false));
    }
  }
);

export const GetAllFolders = createAsyncThunk(
  "/getAllFolders",
  async ({ userEmail }: { userEmail: string }, { dispatch }) => {
    try {
      dispatch(setResponseValue({ name: "pending", value: true }));
      const { data } = await mailService.getFolderList(userEmail);
      return { folders: data.data, userEmail };
    } catch (e: any) {
      console.error("Error fetching emails:", e);
      dispatch(setResponseValue({ name: "error", value: true }));
      dispatch(
        setResponseValue({
          name: "message",
          value: e?.response?.data?.error || e?.response?.data?.message,
        })
      );
    } finally {
      dispatch(setResponseValue({ name: "pending", value: false }));
    }
  }
);

export const SendEmail = createAsyncThunk(
  "/send-email",
  async (
    {
      email,
      to,
      subject,
      body,
      attachments,
      replyTo,
      messageId,
    }: {
      email: string;
      to: string[];
      subject: string | undefined;
      body: any;
      attachments: any[];
      replyTo?: string;
      messageId?: string;
    },
    { dispatch }
  ) => {
    try {
      dispatch(setResponseValue({ name: "pending", value: true }));
      const { data, message } = await mailService.sendEmail(
        {
          email,
          to,
          subject,
          body,
          attachments,
        },
        replyTo ?? "",
        messageId ?? ""
      );
      if (data) {
        dispatch(setResponseValue({ name: "success", value: true }));
        dispatch(setResponseValue({ name: "message", value: message }));
        return data;
      }
    } catch (e: any) {
      console.error("Error fetching emails:", e);
      dispatch(setResponseValue({ name: "error", value: true }));
      dispatch(
        setResponseValue({
          name: "message",
          value: e?.response?.data?.message,
        })
      );
    } finally {
      dispatch(setResponseValue({ name: "pending", value: false }));
    }
  }
);

export const createDraft = createAsyncThunk(
  "/createDraft",
  async (
    {
      email,
      to,
      subject,
      body,
      attachments,
    }: {
      email: string;
      to: string[];
      subject: string | undefined;
      body: string;
      attachments: any[];
    },
    { dispatch }
  ) => {
    try {
      dispatch(setResponseValue({ name: "pending", value: true }));
      const { data, message } = await mailService.createDraft({
        email,
        to,
        subject,
        body,
        attachments,
      });
      if (data) {
        dispatch(setResponseValue({ name: "success", value: true }));
        dispatch(setResponseValue({ name: "message", value: message }));
        return data;
      }
    } catch (e: any) {
      console.error("Error fetching emails:", e);
      dispatch(setResponseValue({ name: "error", value: true }));
      dispatch(
        setResponseValue({
          name: "message",
          value: e?.response?.data?.error || e?.response?.data?.message,
        })
      );
    } finally {
      dispatch(setResponseValue({ name: "pending", value: false }));
      dispatch(
        GetAllDrafts({
          //get updated drafts
          userEmail: email,
          pageToken: "",
        })
      );
    }
  }
);

export const GetAllDrafts = createAsyncThunk(
  "/getAllDrafts",
  async (
    {
      userEmail,
      pageToken,
    }: {
      userEmail: string;
      pageToken: string;
    },
    { dispatch }
  ) => {
    try {
      dispatch(setIsLoading(true));
      dispatch(setResponseValue({ name: "pending", value: true }));
      const { data } = await mailService.getAllDrafts(userEmail, pageToken);
      return data;
    } catch (e: any) {
      console.error("Error fetching emails:", e);
      dispatch(setResponseValue({ name: "error", value: true }));
      dispatch(
        setResponseValue({
          name: "message",
          value: e?.response?.data?.error || e?.response?.data?.message,
        })
      );
    } finally {
      dispatch(setResponseValue({ name: "pending", value: false }));
      dispatch(setIsLoading(false));
    }
  }
);

export const GetDraftById = createAsyncThunk(
  "/getDraftById",
  async (
    { userEmail, draftId }: { userEmail: string; draftId: string },
    { dispatch }
  ) => {
    try {
      dispatch(setIsLoading(true));
      const { data } = await mailService.getDraftById(userEmail, draftId);
      return data.data;
    } catch (e: any) {
      console.error("Error fetching emails:", e);
      dispatch(setResponseValue({ name: "error", value: true }));
      dispatch(
        setResponseValue({
          name: "message",
          value: e?.response?.data?.message,
        })
      );
    } finally {
      dispatch(setIsLoading(false));
    }
  }
);

export const UpdateDraft = createAsyncThunk(
  "/updateDraft",
  async (
    {
      draftId,
      draftDetails,
    }: { draftId: string; draftDetails: IComposeEmailData },
    { dispatch }
  ) => {
    try {
      dispatch(setIsLoading(true));
      const { data } = await mailService.updatedDraft(draftId, draftDetails);
      return data.data;
    } catch (e: any) {
      console.error("Error fetching emails:", e);
      dispatch(setResponseValue({ name: "error", value: true }));
      dispatch(
        setResponseValue({
          name: "message",
          value: e?.response?.data?.message,
        })
      );
    } finally {
      dispatch(setIsLoading(false));
      dispatch(
        GetAllDrafts({
          //get updated drafts
          userEmail: draftDetails.email,
          pageToken: "",
        })
      );
    }
  }
);

export const DeleteDraftById = createAsyncThunk(
  "deleteDraftById",
  async (
    { email, draftId }: { email: string; draftId: string },
    { dispatch }
  ) => {
    try {
      dispatch(setResponseValue({ name: "pending", value: true }));
      const { data, message } = await mailService.deleteDraftById(
        email,
        draftId
      );
      if (data) {
        dispatch(setResponseValue({ name: "success", value: true }));
        dispatch(setResponseValue({ name: "message", value: message }));
      }
    } catch (error: any) {
      dispatch(setResponseValue({ name: "error", value: true }));
      dispatch(
        setResponseValue({
          name: "message",
          value: error?.response?.data?.message,
        })
      );
    } finally {
      dispatch(setResponseValue({ name: "pending", value: false }));
      dispatch(
        GetAllDrafts({
          //get updated drafts
          userEmail: email,
          pageToken: "",
        })
      );
    }
  }
);

export const DownloadEmailAttachmentById = createAsyncThunk(
  "/downloadEmailAttachmentById",
  async (
    {
      email,
      messageId,
      attachmentId,
    }: {
      email: string;
      messageId: string;
      attachmentId: string;
    },
    { dispatch }
  ) => {
    try {
      dispatch(setResponseValue({ name: "pending", value: true }));
      const { data } = await mailService.downloadEmailAttachmentById(
        email,
        messageId,
        attachmentId
      );
      return data;
    } catch (e: any) {
      console.error("Error fetching emails:", e);
      dispatch(setResponseValue({ name: "error", value: true }));
      dispatch(
        setResponseValue({
          name: "message",
          value: e?.response?.data?.message,
        })
      );
    } finally {
      dispatch(setResponseValue({ name: "pending", value: false }));
    }
  }
);

export const ConnectMailBox = createAsyncThunk(
  "/connectGmailProvider",
  async ({ mailboxProvider }: { mailboxProvider: string }, { dispatch }) => {
    try {
      dispatch(setResponseValue({ name: "pending", value: true }));
      const { data } = await mailService.connectMailBox(mailboxProvider);
      if (data) {
        // dispatch(setResponseValue({ name: "success", value: true }));
        // dispatch(setResponseValue({ name: "message", value: message }));
        return data;
      }
    } catch (e: any) {
      console.error("Error fetching emails:", e);
      dispatch(setResponseValue({ name: "error", value: true }));
      dispatch(
        setResponseValue({
          name: "message",
          value: e?.response?.data?.message,
        })
      );
    } finally {
      dispatch(setResponseValue({ name: "pending", value: false }));
    }
  }
);

export const DisConnectMailbox = createAsyncThunk(
  "/dis-connect/GmailProvider",
  async ({ mailboxId }: { mailboxId: string }, { dispatch }) => {
    try {
      dispatch(setResponseValue({ name: "pending", value: true }));
      const { status, message } = await mailService.disConnectMailbox(
        mailboxId
      );
      if (status) {
        dispatch(setResponseValue({ name: "success", value: true }));
        dispatch(setResponseValue({ name: "message", value: message }));
      }
    } catch (e: any) {
      console.error("Error fetching emails:", e);
      dispatch(setResponseValue({ name: "error", value: true }));
      dispatch(
        setResponseValue({
          name: "message",
          value: e?.response?.data?.message,
        })
      );
    } finally {
      dispatch(setResponseValue({ name: "pending", value: false }));
    }
  }
);

export const UpdateEmailStatus = createAsyncThunk(
  "/UpdateEmailUnreadStatus",
  async (
    {
      email,
      mailId,
      unread,
      starred,
    }: { email: string; mailId: string; unread?: boolean; starred?: boolean },
    { dispatch }
  ) => {
    try {
      const { status } = await mailService.updateMailStatus(
        email,
        mailId,
        unread,
        starred
      );
      if (status) {
        return {
          mailId: mailId,
          unread,
          starred,
        };
      }
    } catch (e: any) {
      console.error("Error fetching emails:", e);
      dispatch(setResponseValue({ name: "error", value: true }));
      dispatch(
        setResponseValue({
          name: "message",
          value: e?.response?.data?.message,
        })
      );
    } finally {
      dispatch(setResponseValue({ name: "pending", value: false }));
    }
  }
);

export const GetActiveMailboxProvider = createAsyncThunk(
  "/getActiveMailbox/Provider",
  async (_, { dispatch }) => {
    try {
      dispatch(setResponseValue({ name: "pending", value: true }));
      const { data } = await mailService.getActiveMailboxProvider();
      if (data) {
        // dispatch(setResponseValue({ name: "success", value: true }));
        // dispatch(setResponseValue({ name: "message", value: message }));
        return data;
      }
    } catch (e: any) {
      console.error("Error fetching emails:", e);
      dispatch(setResponseValue({ name: "error", value: true }));
      dispatch(
        setResponseValue({
          name: "message",
          value: e?.response?.data?.message,
        })
      );
    } finally {
      dispatch(setResponseValue({ name: "pending", value: false }));
    }
  }
);

export const DeleteMultipleEmails = createAsyncThunk(
  "/delete/mails",
  async (
    { emailIds, userEmail }: { emailIds: string[]; userEmail: string },
    { dispatch }
  ) => {
    try {
      dispatch(setResponseValue({ name: "pending", value: true }));
      const { message, status } = await mailService.deleteMultipleEmails(
        emailIds,
        userEmail
      );
      console.log("message:", message);
      if (status) {
        dispatch(setResponseValue({ name: "success", value: true }));
        dispatch(setResponseValue({ name: "message", value: message }));
        return {
          emailIds: emailIds,
        };
      }
    } catch (e: any) {
      console.error("Error fetching emails:", e);
      dispatch(setResponseValue({ name: "error", value: true }));
      dispatch(
        setResponseValue({
          name: "message",
          value: e?.response?.data?.message,
        })
      );
    } finally {
      dispatch(setResponseValue({ name: "pending", value: false }));
    }
  }
);

export const GetAllEmailIntegration = createAsyncThunk(
  "/getAllEmailIntegration",
  async (_, { dispatch }) => {
    try {
      dispatch(setResponseValue({ name: "pending", value: true }));
      const { data } = await mailService.getMailboxIntegrationsByOrg();
      return data;
    } catch (e: any) {
      console.error("Error fetching emails:", e);
      dispatch(setResponseValue({ name: "error", value: true }));
      dispatch(
        setResponseValue({
          name: "message",
          value: e?.response?.data?.error || e?.response?.data?.message,
        })
      );
    } finally {
      dispatch(setResponseValue({ name: "pending", value: false }));
    }
  }
);

export const {
  setCurrentEmail,
  setIsLoading,
  setPageNumber,
  setSelectedFolderId,
  resetAllEmails,
  setThreadsLoading,
  updatedCurrentThreads,
} = mailBoxSlice.actions;
export default mailBoxSlice;
